import { commonQuotesService } from '@/services/quotes/common';
import type { CopyQuoteResponse } from '@/services/quotes/types';
import { markClonedQuoteAsDraft } from '@/shared/utils/clone-quote-status.util';
import { useMutation, useQueryClient } from '@tanstack/react-query';

type Variables = {
  policyId: string;
  quoteNumber?: string;
  /** ProductCode explícito da cotação (preferido). Sem ele, tenta derivar dos
   *  6 primeiros dígitos do número da cotação (heurística legada). */
  productCode?: string;
};

function extractProductCode(quoteNumber?: string): string | undefined {
  const digits = (quoteNumber || '').replace(/\D/g, '');
  return digits.length >= 6 ? digits.slice(0, 6) : undefined;
}

export function useCopyQuote() {
  const queryClient = useQueryClient();

  return useMutation<{ policyId: string }, Error, Variables>({
    mutationKey: ['quotes', 'copy'],
    mutationFn: async ({ policyId, quoteNumber, productCode }) => {
      const resolvedProductCode =
        productCode?.trim() || extractProductCode(quoteNumber);
      const response = await commonQuotesService.copyQuote(
        policyId,
        resolvedProductCode,
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Erro ao clonar cotação');
      }

      const data: CopyQuoteResponse = response.data;
      const newPolicyId = (data.id || data.quoteId || '').trim();
      if (!newPolicyId) {
        throw new Error('Resposta inválida ao clonar cotação');
      }

      markClonedQuoteAsDraft(newPolicyId, new Date().toISOString());
      return { policyId: newPolicyId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
    },
  });
}
